import React, { useEffect } from 'react';
import { ShapeElement, TextElement } from '@bynder-studio/render-core';
import { Box, Divider, Tabs } from '@bynder/design-system';
import { IconLinearGradient, IconSolid } from '@bynder/icons';
import { Color, Gradient, LinearGradient, RadialGradient } from '../types';
import { useColorPicker } from './InternalColorPickerContext';
import useEditorSelectedElement from 'packages/hooks/useEditorSelectedElement';
import { getColorType } from '../utils/getColorType';

interface Props {
    value: Color;
    onChange: (newValue: Color) => void;
}

const linearGradient: LinearGradient = {
    type: 'linear',
    angle: 0,
    stops: [
        {
            color: {
                color: '#eeeeee',
                opacity: 100,
            },
            position: 0,
        },
        {
            color: {
                color: '#cccccc',
                opacity: 100,
            },
            position: 100,
        },
    ],
};

const radialGradient: RadialGradient = {
    type: 'radial',
    stops: [
        {
            color: {
                color: '#eeeeee',
                opacity: 100,
            },
            position: 0,
        },
        {
            color: {
                color: '#cccccc',
                opacity: 100,
            },
            position: 100,
        },
    ],
};

const defaultSolidColors = {
    SHAPE: '#CCCCCC',
    TEXT: '#000000',
    GLOBAL: '#FFFFFF',
};

const defaultColors = (elementType: 'SHAPE' | 'TEXT' | 'GLOBAL') => ({
    solid: defaultSolidColors[elementType] || '#FFFFFF',
    linear: linearGradient,
    radial: radialGradient,
});

type ColorType = 'solid' | 'linear' | 'radial';

export function ChangeColorType(props: Props) {
    const { userColors, setUserColor } = useColorPicker();
    const { selectedElement } = useEditorSelectedElement();

    const isShapeElement = selectedElement && selectedElement instanceof ShapeElement;
    const isTextElement = selectedElement && selectedElement instanceof TextElement;

    let elementType: 'SHAPE' | 'TEXT' | 'GLOBAL' = 'GLOBAL';

    if (selectedElement) {
        if (isShapeElement) {
            elementType = 'SHAPE';
        } else if (isTextElement) {
            elementType = 'TEXT';
        }
    }

    const type = getColorType(props.value);

    function handleTypeChange(newType: ColorType) {
        if (newType === type) {
            return;
        }

        const color = (userColors[newType] ? userColors[newType] : defaultColors(elementType)[newType])!;

        props.onChange(color);
    }

    useEffect(() => {
        if (selectedElement) {
            setUserColor(props.value);
        }
    }, [selectedElement]);

    return (
        <Box paddingBottom="4">
            <Box paddingInline="4" paddingBottom="4">
                <Tabs>
                    <Tabs.Tab
                        isActive={type === 'solid'}
                        onClick={(e) => {
                            e.preventDefault();
                            handleTypeChange('solid');
                        }}
                        label="Solid"
                        isLabelHidden
                        icon={<IconSolid />}
                    />
                    <Tabs.Tab
                        isActive={type === 'linear'}
                        onClick={(e) => {
                            e.preventDefault();
                            handleTypeChange('linear');
                        }}
                        label="Linear"
                        isLabelHidden
                        icon={<IconLinearGradient />}
                    />
                    <Tabs.Tab
                        isActive={type === 'radial'}
                        onClick={(e) => {
                            e.preventDefault();
                            handleTypeChange('radial');
                        }}
                        label="Radial"
                        isLabelHidden
                        icon={<CustomRadialGradientIcon />}
                    />
                </Tabs>
            </Box>
            <Divider />
        </Box>
    );
}

function isGradient(color: Color): color is Gradient {
    return typeof color === 'object' && 'stops' in color;
}

// Custom radial gradient icon remove once IconRadialGradient fixed in design system
function CustomRadialGradientIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
            width={24}
            height={24}
            style={{
                verticalAlign: 'middle',
            }}
            aria-hidden="true"
        >
            <mask
                id="b1"
                width={18}
                height={18}
                x={3}
                y={3}
                maskUnits="userSpaceOnUse"
                style={{
                    maskType: 'alpha',
                }}
            >
                <path fill="url(#a1)" d="M3 3h18v18H3z" />
            </mask>
            <g mask="url(#b1)">
                <path
                    fill="currentColor"
                    d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2Z"
                />
            </g>
            <defs>
                <radialGradient
                    id="a1"
                    cx={0}
                    cy={0}
                    r={1}
                    gradientTransform="matrix(0 9 -9 0 12 12)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop />
                    <stop offset={1} stopOpacity={0} />
                </radialGradient>
            </defs>
        </svg>
    );
}
